import React from "react";
import { Banner, Button, FormField, Input, StatusPill, type StatusTone } from "@/ds";
import { cockpit, ApiError } from "@/api";
import { emailQaReportUrl, type EmailQaCategory, type EmailQaDetail, type EmailQaMessage,
  type EmailQaRun, type EmailQaState, type EmailQaStatus, type EmailQaVerdict } from "@/api/email-qa-types";
import { useLang, useT } from "../i18n";
import { useRoutedState } from "@/lib/routed-state";
import type { EmailQaSection } from "../router";
import { EmailQaNav } from "./email-qa-nav";
import styles from "./screen-email-qa.module.css";

const active = (run: EmailQaRun) => run.state === "queued" || run.state === "running";
const tones: Record<EmailQaState, StatusTone> = {
  queued: "neutral", running: "progress", passed: "success", failed: "danger", blocked: "warning", cancelled: "neutral",
};
const verdictTones: Record<EmailQaVerdict, StatusTone> = { PASS: "success", FAIL: "danger", BLOCKED: "warning" };
const money = (microUsd: number) => (microUsd / 1_000_000).toFixed(2);

export function deliveredEmailQaMessages(run: EmailQaDetail): EmailQaMessage[] {
  if (run.data.review_input) return run.data.review_input.messages;
  const messages: EmailQaMessage[] = [];
  run.data.customer_messages.forEach((message, index) => {
    if (message.ingested_message_id) messages.push({ id: message.ingested_message_id, direction: "inbound",
      subject: message.subject, text: message.text });
    const receipt = run.data.turns[index]?.receipt;
    if (receipt) messages.push({ id: receipt.rfc_message_id, direction: "outbound", subject: receipt.subject, text: receipt.text });
  });
  return messages;
}

function ErrorNotice({ error, reload }: { error: unknown; reload: () => void }) {
  const t = useT();
  const code = error instanceof ApiError ? (error.data as { code?: string })?.code : null;
  return <Banner tone="danger" title={t("emailQa.error")}>
    {code && <code>{code}</code>}
    <Button variant="secondary" size="sm" onClick={reload}>{t("emailQa.retry")}</Button>
  </Banner>;
}

export interface EmailQaScreenProps {
  /**
   * #1228: the section and the open run as the URL states them. Absent means the screen keeps
   * both in its own state.
   */
  section?: Exclude<EmailQaSection, "test-mail">;
  onSectionChange?: (section: Exclude<EmailQaSection, "test-mail">) => void;
  runId?: string | null;
  onRunChange?: (runId: string | null) => void;
  onOpenTestMail?: () => void;
}

export function EmailQaScreen({
  section: routedSection,
  onSectionChange,
  runId: routedRunId,
  onRunChange,
  onOpenTestMail,
}: EmailQaScreenProps = {}) {
  const t = useT();
  const { lang } = useLang();
  const [status, setStatus] = React.useState<EmailQaStatus | null>(null);
  const [runs, setRuns] = React.useState<EmailQaRun[]>([]);
  const [selectedId, setSelectedId] = useRoutedState<string | null>(routedRunId, onRunChange, null);
  const [detail, setDetail] = React.useState<EmailQaDetail | null>(null);
  const [section, setSection] = useRoutedState<Exclude<EmailQaSection, "test-mail">>(routedSection, onSectionChange, "runs");
  const [filter, setFilter] = React.useState("all");
  const [count, setCount] = React.useState("1");
  const [budget, setBudget] = React.useState("5");
  const [loading, setLoading] = React.useState(true);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [starting, setStarting] = React.useState(false);
  const [cancelling, setCancelling] = React.useState(false);
  const [error, setError] = React.useState<unknown>(null);
  const [detailError, setDetailError] = React.useState<unknown>(null);
  const [reload, setReload] = React.useState(0);
  const requestKey = React.useRef<{ parameters: string; id: string } | null>(null);
  // The run poller decides what to select from inside a closure, and the selection may live in
  // the URL now, so it reads the current value through a ref rather than a state updater.
  const selectedRef = React.useRef(selectedId);
  selectedRef.current = selectedId;
  const sectionRef = React.useRef(section);
  sectionRef.current = section;
  const tenantId = status?.tenant?.id ?? null;
  const refresh = () => setReload((value) => value + 1);

  React.useEffect(() => {
    let disposed = false;
    setError(null);
    cockpit.getEmailQaStatus().then((next) => { if (!disposed) setStatus(next); })
      .catch((cause) => { if (!disposed) setError(cause); })
      .finally(() => { if (!disposed) setLoading(false); });
    return () => { disposed = true; };
  }, [reload]);

  React.useEffect(() => {
    if (!tenantId) return;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout>;
    const load = async () => {
      try {
        const next = await cockpit.listEmailQaRuns(tenantId);
        if (disposed) return;
        setRuns(next.runs);
        // Nothing chosen yet, or the URL names a run this tenant does not have: fall to the
        // newest one, which is also what makes an addressed run survive a refresh untouched.
        // Written only when it actually differs — this poller runs every few seconds, and the
        // selection now lives in the address, where a no-op write is a history rewrite.
        const current = selectedRef.current;
        const desired = current && next.runs.some((run) => run.id === current)
          ? current
          : next.runs[0]?.id ?? null;
        // Background discovery must not navigate away from the instructions URL.
        if (sectionRef.current === "runs" && desired !== current) setSelectedId(desired);
        setError(null);
        timer = setTimeout(load, next.runs.some(active) ? 5000 : 30000);
      } catch (cause) {
        if (!disposed) { setError(cause); timer = setTimeout(load, 15000); }
      }
    };
    void load();
    return () => { disposed = true; clearTimeout(timer); };
  }, [tenantId, reload]);

  React.useEffect(() => {
    // Returning to Runs uses the already-loaded list immediately, without waiting for its poll.
    if (section === "runs" && !selectedId && runs.length) setSelectedId(runs[0].id);
  }, [section, selectedId, runs, setSelectedId]);

  React.useEffect(() => {
    setDetail(null);
    setDetailError(null);
    if (!tenantId || !selectedId) return;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout>;
    setDetailLoading(true);
    const load = async () => {
      try {
        const next = await cockpit.getEmailQaRun(tenantId, selectedId);
        if (disposed) return;
        setDetail(next);
        setDetailError(null);
        if (active(next)) timer = setTimeout(load, 5000);
      } catch (cause) {
        if (!disposed) setDetailError(cause);
      } finally { if (!disposed) setDetailLoading(false); }
    };
    void load();
    return () => { disposed = true; clearTimeout(timer); };
  }, [tenantId, selectedId, reload]);

  const start = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!tenantId || !status?.available || starting) return;
    const customers = Number(count);
    const dollars = Number(budget);
    if (!Number.isInteger(customers) || customers < 1 || customers > status.limits.batch
        || !Number.isFinite(dollars) || dollars < 1 || dollars * 1_000_000 > status.limits.max_budget_micro_usd) return;
    const parameters = JSON.stringify([tenantId, customers, dollars, lang]);
    if (requestKey.current?.parameters !== parameters) requestKey.current = { parameters, id: crypto.randomUUID() };
    setStarting(true);
    setError(null);
    try {
      const result = await cockpit.startEmailQaBatch(tenantId, { batch_id: requestKey.current.id,
        count: customers, budget_micro_usd: Math.round(dollars * 1_000_000), language: lang });
      requestKey.current = null;
      setSelectedId(result.runs[0]?.id ?? null);
      setSection("runs");
      refresh();
    } catch (cause) { setError(cause); }
    finally { setStarting(false); }
  };

  const cancel = async () => {
    if (!tenantId || !detail || cancelling) return;
    setCancelling(true);
    try { setDetail(await cockpit.cancelEmailQaRun(tenantId, detail.id)); refresh(); }
    catch (cause) { setError(cause); }
    finally { setCancelling(false); }
  };

  const filtered = runs.filter((run) => filter === "all" || (filter === "active" ? active(run)
    : run.state === "failed" || run.state === "blocked"));
  const amount = Number(count) * Number(budget);
  return <div className={`page page--management ${styles.page}`}>
    <div className="page-head"><div><h1>{t("emailQa.title")}</h1><p>{t("emailQa.sub")}</p></div></div>
    {error ? <ErrorNotice error={error} reload={refresh} /> : null}
    {loading && <p role="status">{t("emailQa.loading")}</p>}
    {status && <>
      {!status.available && <Banner tone="warning" title={t("emailQa.unavailable")}>
        <p>{t("emailQa.unavailableCopy")}</p>
        {status.reason && <code>{status.reason}</code>}
      </Banner>}
      <form className={styles.launch} onSubmit={(event) => void start(event)}>
        <div className={styles.launchFields}>
          <FormField label={t("emailQa.count")}><Input type="number" min={1} max={status.limits.batch} step={1}
            value={count} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCount(event.target.value)} required disabled={starting} /></FormField>
          <FormField label={t("emailQa.budget")}><Input type="number" min={1} max={status.limits.max_budget_micro_usd / 1_000_000} step={1}
            value={budget} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setBudget(event.target.value)} required disabled={starting} /></FormField>
          <Button type="submit" loading={starting} disabled={!status.available || starting}>
            {Number(count) === 1 ? t("emailQa.newRun") : t("emailQa.startBatch", { count })}
          </Button>
        </div>
        <p className={styles.muted}>{t("emailQa.budgetHint", { amount: Number.isFinite(amount) ? amount.toFixed(2) : "—" })}</p>
        {status.tenant && <p className={styles.muted}>{t("emailQa.mailbox", { name: status.tenant.name, mailbox: status.mailbox_address ?? "—" })}</p>}
        {status.ai_quota && <p className={styles.muted}>{t("emailQa.quota", { used: status.ai_quota.usage, limit: status.ai_quota.limit })}</p>}
      </form>
    </>}
    <EmailQaNav section={section} owner testMailEnabled={!!onOpenTestMail}
      onChange={(name) => name === "test-mail" ? onOpenTestMail?.() : setSection(name)} />
    <div id="email-qa-panel" role="tabpanel" aria-labelledby={`email-qa-panel-tab-${section}`} tabIndex={0}>
    {status && <>
      {section === "instructions" ? <div className={styles.instructions}>
        {(["scenario", "reply", "review"] as const).map((kind) => <section className={styles.panel} key={kind}>
          <h2>{t(`emailQa.${kind}`)}</h2>
          <p>{status.models[kind].model} · {status.models[kind].reasoning_effort}</p>
          <p className={styles.muted}>{status.models[kind].prompt_version}</p>
          <pre>{status.models[kind].instructions ?? t("emailQa.instructionsUnavailable")}</pre>
        </section>)}
      </div> : <div className={styles.workspace}>
        <aside className={styles.runList} aria-label={t("emailQa.runs")}>
          <label className={styles.filter}>{t("emailQa.filter")}<select className="ribbon-field" value={filter} onChange={(event) => setFilter(event.target.value)}>
            {(["all", "active", "issues"] as const).map((value) => <option key={value} value={value}>{t(`emailQa.${value}`)}</option>)}
          </select></label>
          <p className={styles.muted}>{t("emailQa.latest")}</p>
          {!filtered.length && <div className={styles.empty}><h2>{t("emailQa.empty")}</h2><p>{t("emailQa.emptyCopy")}</p></div>}
          {filtered.map((run) => <button type="button" className={styles.run} data-selected={selectedId === run.id} key={run.id}
            aria-pressed={selectedId === run.id} onClick={() => setSelectedId(run.id)}>
            <span className={styles.runTop}><strong>#{run.customer_number}</strong><StatusPill size="sm" tone={tones[run.state]} label={t(`emailQa.state.${run.state}`)} /></span>
            <span>{run.title || run.customer_address}</span>
            {run.title && run.title !== run.customer_address && <small>{run.customer_address}</small>}
            <small>{new Date(run.created_at).toLocaleString(lang)}</small>
          </button>)}
        </aside>
        <section className={styles.detail} aria-label={t("emailQa.report")} aria-busy={detailLoading}>
          {detailError ? <ErrorNotice error={detailError} reload={refresh} /> : null}
          {detailLoading ? <p role="status">{t("emailQa.loading")}</p> : detail ? <>
            <div className={styles.detailHead}>
              <div><StatusPill tone={tones[detail.state]} label={t(`emailQa.state.${detail.state}`)} />
                <h2>{detail.title || detail.customer_address}</h2>
                {detail.title && detail.title !== detail.customer_address && <p>{detail.customer_address}</p>}
                <p className={styles.muted}>{t("emailQa.used", { used: money(detail.used_micro_usd), budget: money(detail.budget_micro_usd) })}
                  {" · "}{t("emailQa.replies", { count: detail.platform_replies })}</p>
              </div>
              <div className={styles.actions}>
                <a href={emailQaReportUrl(detail.tenant_id, detail.id, "md")} download>{t("emailQa.exportMd")}</a>
                <a href={emailQaReportUrl(detail.tenant_id, detail.id, "json")} download>{t("emailQa.exportJson")}</a>
                {active(detail) && <Button variant="secondary" size="sm" loading={detail.cancel_requested || cancelling} disabled={detail.cancel_requested || cancelling} onClick={() => void cancel()}>
                  {t("emailQa.cancel")}</Button>}
              </div>
            </div>
            {active(detail) && <div className={styles.progress} role="status">
              <strong>{detail.cancel_requested ? t("emailQa.cancelling") : t(`emailQa.stage.${detail.stage}`)}</strong>
              <span>{detail.cancel_requested ? t("emailQa.cancelHint") : t("emailQa.updated", { time: new Date(detail.updated_at).toLocaleTimeString(lang) })}</span>
            </div>}
            {detail.usage_uncertain && <p className={styles.muted}>{t("emailQa.reserved")}</p>}
            <ReviewResult run={detail} />
            <section className={styles.panel}><h2>{t("emailQa.dialogue")}</h2>
              <div className={styles.messages}>
                {!deliveredEmailQaMessages(detail).length && <p>{t("emailQa.noMessages")}</p>}
                {deliveredEmailQaMessages(detail).map((message, index) => <article key={message.id} id={`qa-message-${encodeURIComponent(message.id)}`}
                  className={styles.message} data-direction={message.direction} tabIndex={-1}>
                  <div className={styles.messageMeta}>{index + 1} · {t(message.direction === "inbound" ? "emailQa.customer" : "emailQa.platform")}</div>
                  <h3>{message.subject}</h3><pre>{message.text}</pre>
                </article>)}
              </div>
            </section>
            <details className={styles.panel}><summary>{t("emailQa.profile")}</summary><pre>{detail.data.scenario?.profile ?? "—"}</pre></details>
            <details className={styles.panel}><summary>{t("emailQa.evidence")}</summary>
              <h3>{t("emailQa.sourceHash")}</h3><code>{detail.data.source_hash ?? "—"}</code>
              <h3>{t("emailQa.calls")}</h3>
              {detail.data.calls.map((call) => <div className={styles.call} key={call.id}>
                <strong>{t(`emailQa.${call.kind}`)}</strong><span>{call.model} · {call.reasoning_effort}</span>
                <span>{call.input_tokens ?? "?"} / {call.output_tokens ?? "?"} tokens · {call.prompt_version}</span>
                <code>{call.response_id ?? call.error_code ?? call.state}</code>
              </div>)}
              <h3>{t("emailQa.checks")}</h3>
              <ul>{detail.data.checks.map((check, index) => <li key={index}>{check.verdict}: <code>{check.code}</code></li>)}</ul>
              {detail.terminal_reason && <code>{detail.terminal_reason}</code>}
            </details>
          </> : <div className={styles.empty}>{t("emailQa.select")}</div>}
        </section>
      </div>}
    </>}
    </div>
  </div>;
}

function ReviewResult({ run }: { run: EmailQaDetail }) {
  const t = useT();
  const review = run.data.review;
  return <section className={styles.panel}>
    <h2>{t("emailQa.report")}</h2>
    {review ? <>
      <p className={styles.reviewSummary}>{review.summary}</p>
      <div className={styles.categories}>
        {(Object.keys(review.categories) as EmailQaCategory[]).map((category) => <div key={category}>
          <div className={styles.categoryHead}><h3>{t(`emailQa.category.${category}`)}</h3>
            <StatusPill size="sm" tone={verdictTones[review.categories[category].verdict]} label={t(`emailQa.verdict.${review.categories[category].verdict}`)} /></div>
          <p>{review.categories[category].note}</p>
        </div>)}
      </div>
      {!!review.findings.length && <h3>{t("emailQa.findings")}</h3>}
      {review.findings.map((finding, index) => <article className={styles.finding} key={index}>
        <h4>{t(`emailQa.category.${finding.category}`)}</h4><blockquote>{finding.quote}</blockquote>
        <p>{finding.explanation}</p><p><strong>{t("emailQa.expected")}: </strong>{finding.expected}</p>
        <Button variant="ghost" size="sm" onClick={() => {
          const message = document.getElementById(`qa-message-${encodeURIComponent(finding.message_id)}`);
          message?.scrollIntoView({ block: "center" });
          message?.focus({ preventScroll: true });
        }}>{t("emailQa.showMessage")}</Button>
      </article>)}
      <p className={styles.muted}>{t("emailQa.diagnostic")}</p>
    </> : <p>{t(active(run) ? "emailQa.pendingReview" : "emailQa.noReview")}</p>}
  </section>;
}
